// src/pages/marketplace/tenant/TenantProductDetailPage.tsx
// Detailed view of a tenant's product subscription with usage, billing, and dependency info.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map ProductTenantStatus to StatusBadge variant. */
function statusVariant(status: string): string {
  switch (status) {
    case 'PRODUCT_TENANT_STATUS_ACTIVE':
      return 'success';
    case 'PRODUCT_TENANT_STATUS_SUSPENDED':
      return 'error';
    case 'PRODUCT_TENANT_STATUS_PENDING_SETUP':
      return 'info';
    case 'PRODUCT_TENANT_STATUS_CANCELLED':
      return 'secondary';
    default:
      return 'warning';
  }
}

/** Human-readable status label. */
function statusLabel(status: string, t: (k: string) => string): string {
  switch (status) {
    case 'PRODUCT_TENANT_STATUS_ACTIVE':
      return t('marketplace.status.active');
    case 'PRODUCT_TENANT_STATUS_SUSPENDED':
      return t('marketplace.status.suspended');
    case 'PRODUCT_TENANT_STATUS_PENDING_SETUP':
      return t('marketplace.status.pendingSetup');
    case 'PRODUCT_TENANT_STATUS_CANCELLED':
      return t('marketplace.status.cancelled');
    default:
      return status;
  }
}

/** Percentage of usage (clamped to 0-100). */
function usagePercent(current: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(Math.round((current / limit) * 100), 100);
}

/** Color class for usage bar fill. */
function usageBarColor(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 70) return 'bg-yellow-500';
  return 'bg-green-500';
}

/** Color class for usage percentage text. */
function usageTextColor(percent: number): string {
  if (percent >= 90) return 'text-red-600 dark:text-red-400';
  if (percent >= 70) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}

/** Calculate remaining trial days from an ISO timestamp. */
function trialDaysLeft(trialEndsAt: string | undefined): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  const now = Date.now();
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

/** Format ISO date to localized string. */
function formatDate(iso: string | undefined): string {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantProductDetailPage - detailed subscription view for a single product.
 * Route: /settings/products/:id
 */
export const TenantProductDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useProduct();
  const { t } = useI18n();

  const [dependencies, setDependencies] = useState<any[]>([]);
  const [depsLoading, setDepsLoading] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Product plus optional subscription-shaped extras (is_trial, usage_limits, ...)
  // that the regenerated Product type no longer carries.
  const item = store.currentItem as any;

  // Fetch product detail (populates store.currentItem)
  useEffect(() => {
    if (id) {
      store.GetProduct(id);
    }
    return () => {
      store.clearCurrent?.();
    };
  }, [id]);

  // Fetch dependencies
  useEffect(() => {
    if (id) {
      setDepsLoading(true);
      store
        .ListProductDependencies(id)
        .then((res: any) => {
          const depItems = res?.dependencies?.items;
          setDependencies(Array.isArray(depItems) ? depItems : []);
        })
        .catch(() => {
          // handled by store
        })
        .finally(() => {
          setDepsLoading(false);
        });
    }
  }, [id]);

  const handleCancel = async () => {
    if (!id) return;
    setCancelling(true);
    try {
      // TODO: Call actual cancel subscription API
      navigate('/settings/products');
    } catch {
      // Error handled by store
    } finally {
      setCancelling(false);
      setCancelConfirm(false);
    }
  };

  // ---------------------------------------------------------------------------
  // States
  // ---------------------------------------------------------------------------

  if (store.isLoading && !item) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link
          to="/settings/products"
          className="mt-2 inline-block text-sm underline text-primary"
        >
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link
          to="/settings/products"
          className="mt-2 inline-block text-sm underline text-primary"
        >
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  // Resolve product data -- could be nested or flat depending on store shape
  const product = item.product ?? item;
  const productName = product.name ?? '';
  const productTagline = product.tagline ?? '';
  const iconUrl = product.icon_url;
  const colorPrimary = product.color_primary || '#6366f1';
  const status = item.status ?? 'PRODUCT_TENANT_STATUS_ACTIVE';
  const isTrial = item.is_trial;
  const trialEndsAt = item.trial_ends_at;
  const daysLeft = trialDaysLeft(trialEndsAt);
  const usageLimits: Record<string, number> = item.usage_limits ?? {};
  const currentUsage: Record<string, number> = item.current_usage ?? {};
  const createdAt = item.created_at;
  const planEntity = item.plan;
  const planName = planEntity?.name ?? planEntity?.slug ?? t('marketplace.detail.defaultPlan');

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/settings" className="text-muted-foreground hover:text-foreground">
          {t('common.settings')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/settings/products" className="text-muted-foreground hover:text-foreground">
          {t('marketplace.tenant.title')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{productName}</span>
      </nav>

      {/* Header */}
      <div className="flex items-start gap-4">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={productName}
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
          />
        ) : (
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-xl"
            style={{ backgroundColor: colorPrimary }}
          >
            {productName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground">{productName}</h1>
            <StatusBadge
              variant={statusVariant(status)}
              label={statusLabel(status, t)}
            />
          </div>
          {productTagline && (
            <p className="text-sm text-muted-foreground mt-1">{productTagline}</p>
          )}
        </div>
      </div>

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* =============================================================== */}
        {/* Subscription Info */}
        {/* =============================================================== */}
        <Card>
          <CardHeader>
            <CardTitle>{t('marketplace.detail.subscription.title')}</CardTitle>
            <CardDescription>
              {t('marketplace.detail.subscription.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div className="flex items-center justify-between">
                <dt className="text-sm text-muted-foreground">
                  {t('marketplace.detail.subscription.plan')}
                </dt>
                <dd className="text-sm font-medium text-foreground">{planName}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm text-muted-foreground">
                  {t('marketplace.detail.subscription.status')}
                </dt>
                <dd>
                  <StatusBadge
                    variant={statusVariant(status)}
                    label={statusLabel(status, t)}
                    size="sm"
                  />
                </dd>
              </div>
              {isTrial && daysLeft !== null && (
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-muted-foreground">
                    {t('marketplace.detail.subscription.trial')}
                  </dt>
                  <dd>
                    <Badge variant={daysLeft <= 7 ? 'warning' : 'default'} size="sm">
                      {daysLeft > 0
                        ? t('marketplace.tenant.trialDays', { count: daysLeft })
                        : t('marketplace.tenant.trialExpired')}
                    </Badge>
                  </dd>
                </div>
              )}
              <div className="flex items-center justify-between">
                <dt className="text-sm text-muted-foreground">
                  {t('marketplace.detail.subscription.startDate')}
                </dt>
                <dd className="text-sm text-foreground">{formatDate(createdAt)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm text-muted-foreground">
                  {t('marketplace.detail.subscription.nextBilling')}
                </dt>
                <dd className="text-sm text-muted-foreground">--</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* =============================================================== */}
        {/* Usage */}
        {/* =============================================================== */}
        <Card>
          <CardHeader>
            <CardTitle>{t('marketplace.detail.usage.title')}</CardTitle>
            <CardDescription>
              {t('marketplace.detail.usage.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(usageLimits).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('marketplace.detail.usage.noLimits')}
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(usageLimits).map(([key, limit]) => {
                  const current = currentUsage[key] ?? 0;
                  const percent = usagePercent(current, limit as number);
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-foreground capitalize">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className={`text-sm font-medium ${usageTextColor(percent)}`}>
                          {current} {t('marketplace.detail.usage.of')} {limit}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${usageBarColor(percent)}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 text-right">
                        {percent}%
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* =============================================================== */}
        {/* Billing */}
        {/* =============================================================== */}
        <Card>
          <CardHeader>
            <CardTitle>{t('marketplace.detail.billing.title')}</CardTitle>
            <CardDescription>
              {t('marketplace.detail.billing.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div className="flex items-center justify-between">
                <dt className="text-sm text-muted-foreground">
                  {t('marketplace.detail.billing.currentCost')}
                </dt>
                <dd className="text-sm font-medium text-foreground">--</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm text-muted-foreground">
                  {t('marketplace.detail.billing.billingAccount')}
                </dt>
                <dd className="text-sm text-foreground">
                  {item.billing_account?.id ? (
                    <Link
                      to={`/settings/billing/${item.billing_account.id}`}
                      className="text-primary hover:underline"
                    >
                      {t('marketplace.detail.billing.viewAccount')}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </dd>
              </div>
            </dl>
          </CardContent>
          <CardFooter className="flex items-center gap-2">
            <Button variant="secondary" size="sm">
              {t('marketplace.detail.billing.changePlan')}
            </Button>
            <Button variant="ghost" size="sm">
              {t('marketplace.detail.billing.viewInvoices')}
            </Button>
          </CardFooter>
        </Card>

        {/* =============================================================== */}
        {/* Dependencies */}
        {/* =============================================================== */}
        <Card>
          <CardHeader>
            <CardTitle>{t('marketplace.detail.dependencies.title')}</CardTitle>
            <CardDescription>
              {t('marketplace.detail.dependencies.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {depsLoading ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('common.loading')}
              </p>
            ) : dependencies.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('marketplace.detail.dependencies.none')}
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {dependencies.map((dep: any, idx: number) => {
                  const reqProduct = dep.required_product;
                  const reqName =
                    reqProduct?.name ?? reqProduct?.slug ?? reqProduct?.id ?? '--';
                  const reqId = reqProduct?.entity?.id ?? reqProduct?.id;
                  const requirement = dep.requirement ?? '';
                  const isHard =
                    requirement === 'PRODUCT_DEPENDENCY_REQUIREMENT_HARD';
                  const isSoft =
                    requirement === 'PRODUCT_DEPENDENCY_REQUIREMENT_SOFT';

                  return (
                    <li key={idx} className="py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {reqName}
                        </p>
                        {dep.description && (
                          <p className="text-xs text-muted-foreground truncate">
                            {dep.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge
                          variant={isHard ? 'error' : isSoft ? 'warning' : 'default'}
                          size="sm"
                        >
                          {isHard
                            ? t('marketplace.detail.dependencies.required')
                            : isSoft
                              ? t('marketplace.detail.dependencies.optional')
                              : t('marketplace.detail.dependencies.recommended')}
                        </Badge>
                        {reqId && (
                          <Link to={`/settings/products/${reqId}`}>
                            <Button variant="ghost" size="sm">
                              {t('common.view')}
                            </Button>
                          </Link>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ================================================================= */}
      {/* Danger zone */}
      {/* ================================================================= */}
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader>
          <CardTitle className="text-red-600 dark:text-red-400">
            {t('marketplace.detail.danger.title')}
          </CardTitle>
          <CardDescription>
            {t('marketplace.detail.danger.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {cancelConfirm ? (
            <div className="space-y-4">
              <ErrorAlert error={t('marketplace.detail.danger.confirmMessage')} />
              <div className="flex items-center gap-3">
                <Button
                  variant="danger"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling
                    ? t('common.loading')
                    : t('marketplace.detail.danger.confirmButton')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setCancelConfirm(false)}
                  disabled={cancelling}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="danger" onClick={() => setCancelConfirm(true)}>
              {t('marketplace.detail.danger.cancelSubscription')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default TenantProductDetailPage;
